import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, formatErr } from "../../lib/api";
import { Loading, StatusPill } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";

const FLOW = ["requested", "acknowledged", "arranging", "scheduled", "in_progress", "completed"];
const LABELS = { requested: "Requested", acknowledged: "Acknowledged", arranging: "Being arranged", scheduled: "Scheduled", in_progress: "In progress", completed: "Completed", cancelled: "Cancelled" };
const PROVIDER_TYPE_MAP = {
  "Doctor Visit Coordination": ["doctor", "hospital"],
  "Ambulance Coordination": ["ambulance"],
  "Nurse": ["nursing"],
  "Attendant / Caregiver": ["attendant", "nursing"],
  "Physiotherapist": ["physiotherapy"],
  "Diagnostics / Home Collection": ["diagnostics"],
  "Pharmacy / Medicine Assistance": ["pharmacy"],
};

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="shrink-0 text-stone-500">{label}</span>
      <span className="text-right font-semibold text-stone-800">{children ?? "—"}</span>
    </div>
  );
}

export default function ServiceRequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canManage = ["ops_manager", "admin", "super_admin"].includes(user?.role);
  const [d, setD] = useState(null);
  const [providers, setProviders] = useState([]);
  const [cms, setCms] = useState([]);
  const [form, setForm] = useState({ status: "", provider_id: "", provider_name: "", provider_ref: "", scheduled_at: "", cm_id: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/service-requests/${id}`).then((r) => setD(r.data)).catch((e) => { toast.error(formatErr(e)); setD({ error: true }); });
  useEffect(() => {
    load();
    api.get("/admin/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/admin/care-managers").then((r) => setCms(r.data)).catch(() => {});
  }, [id]);

  if (!d) return <Loading />;
  if (d.error) return <p className="p-6 text-sm text-stone-500" data-testid="sr-error">Request not found or unavailable.</p>;
  const r = d.request;
  const closed = ["completed", "cancelled"].includes(r.status);
  const curIdx = FLOW.indexOf(r.status);
  const nextStatus = curIdx >= 0 && curIdx < FLOW.length - 1 ? FLOW[curIdx + 1] : null;
  const suggested = PROVIDER_TYPE_MAP[r.type] || null;
  const sortedProviders = suggested
    ? [...providers.filter((p) => suggested.includes(p.type)), ...providers.filter((p) => !suggested.includes(p.type))]
    : providers;

  const quick = async (payload, msg) => {
    setBusy(true);
    try { await api.post(`/admin/service-requests/${id}/update`, payload); toast.success(msg); await load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const save = async () => {
    const payload = {};
    if (form.status && form.status !== r.status) payload.status = form.status;
    if (form.provider_id) payload.provider_id = form.provider_id;
    else if (form.provider_name.trim()) payload.provider_name = form.provider_name.trim();
    if (form.provider_ref.trim()) payload.provider_ref = form.provider_ref.trim();
    if (form.scheduled_at) payload.scheduled_at = form.scheduled_at;
    if (form.cm_id) payload.cm_id = form.cm_id;
    if (form.note.trim()) payload.note = form.note.trim();
    if (!Object.keys(payload).length) { toast.error("Nothing to update"); return; }
    await quick(payload, "Request updated — family notified");
    setForm({ status: "", provider_id: "", provider_name: "", provider_ref: "", scheduled_at: "", cm_id: "", note: "" });
  };

  const phoneDigits = (d.owner?.phone || "").replace(/\D/g, "");

  return (
    <div data-testid="sr-detail">
      <Link to="/app/admin/requests" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-emerald-900" data-testid="sr-back">
        <ArrowLeft className="h-4 w-4" /> Service Requests
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-stone-900">{r.type}</h1>
        <StatusPill status={r.status} />
      </div>
      <p className="mt-1 text-sm text-stone-500">
        {d.family?.name}{d.parent ? ` · for ${d.parent.name}` : ""} · raised {fmtDate(r.created_at)}
        {r.urgency === "urgent" && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">URGENT</span>}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-1" data-testid="sr-flow">
        {FLOW.map((s, i) => (
          <React.Fragment key={s}>
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${r.status !== "cancelled" && i <= curIdx ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-400"}`}>{LABELS[s]}</span>
            {i < FLOW.length - 1 && <span className={`h-0.5 w-3 sm:flex-1 ${i < curIdx ? "bg-emerald-800" : "bg-stone-200"}`} />}
          </React.Fragment>
        ))}
        {r.status === "cancelled" && <span className="rounded-full bg-stone-200 px-2.5 py-1 text-[10px] font-bold uppercase text-stone-500">Cancelled</span>}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <div className="p1-card p-5" data-testid="sr-family-card">
            <h2 className="font-heading text-base font-semibold text-stone-900">Family & parent</h2>
            <div className="mt-2 divide-y divide-stone-50">
              <Row label="Family">
                <Link to={`/app/admin/customers?id=${r.family_id}`} className="text-emerald-900 underline" data-testid="sr-family-link">{d.family?.name || "—"}</Link>
              </Row>
              <Row label="Parent">{d.parent?.name}</Row>
              <Row label="Address / area">{d.parent ? `${d.parent.address || ""}${d.parent.zone ? ` · ${d.parent.zone}` : ""}${d.parent.city ? `, ${d.parent.city}` : ""}` : (d.family?.city || "—")}</Row>
              <Row label="Plan">{d.plan ? `${d.plan.name}` : "No plan"}</Row>
              <Row label="Family Care Manager">{d.family_care_manager?.name || "Unassigned"}</Row>
            </div>
            {d.owner && (
              <div className="mt-3 rounded-xl bg-stone-50 p-3" data-testid="sr-contact">
                <p className="text-xs font-semibold text-stone-500">Family contact — {d.owner.name}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {d.owner.phone && <a href={`tel:${d.owner.phone}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-900 px-3 py-1.5 text-xs font-bold text-white" data-testid="sr-call"><Phone className="h-3.5 w-3.5" /> {d.owner.phone}</a>}
                  {phoneDigits && <a href={`https://wa.me/${phoneDigits}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-emerald-800 px-3 py-1.5 text-xs font-bold text-emerald-900" data-testid="sr-whatsapp"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>}
                  {d.owner.email && <a href={`mailto:${d.owner.email}`} className="inline-flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600" data-testid="sr-email"><Mail className="h-3.5 w-3.5" /> Email</a>}
                </div>
              </div>
            )}
          </div>

          <div className="p1-card p-5" data-testid="sr-request-card">
            <h2 className="font-heading text-base font-semibold text-stone-900">Request details</h2>
            <div className="mt-2 divide-y divide-stone-50">
              <Row label="Requested on">{fmtDate(r.created_at)}</Row>
              <Row label="Preferred time">{r.preferred_time || "—"}</Row>
              <Row label="Urgency"><span className="capitalize">{r.urgency || "normal"}</span></Row>
              <Row label="Notes from family">{r.details || "—"}</Row>
            </div>
          </div>

          <div className="p1-card p-5" data-testid="sr-fulfilment-card">
            <h2 className="font-heading text-base font-semibold text-stone-900">Fulfilment</h2>
            <div className="mt-2 divide-y divide-stone-50">
              <Row label="Provider">{r.provider_name || "Not assigned"}</Row>
              <Row label="Provider ref / ETA">{r.provider_ref || "—"}</Row>
              <Row label="Scheduled for">{r.scheduled_at ? fmtDate(r.scheduled_at) : "—"}</Row>
              <Row label="Request Care Manager">{d.request_care_manager?.name || r.cm_name || "—"}</Row>
              {r.cost_estimate && <Row label="Cost estimate">{r.cost_estimate}</Row>}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          {canManage && !closed && (
            <div className="p1-card p-5" data-testid="sr-actions">
              <h2 className="font-heading text-base font-semibold text-stone-900">Manage request</h2>
              {r.status === "requested" && (
                <button onClick={() => quick({ status: "acknowledged" }, "Request acknowledged — family notified")} disabled={busy}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-900 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50" data-testid="sr-acknowledge">
                  <CheckCircle2 className="h-4 w-4" /> Acknowledge request
                </button>
              )}
              <div className="mt-4 space-y-3">
                <div>
                  <label className="p1-label">Update status</label>
                  <select className="p1-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="sr-status">
                    <option value="">— Keep {LABELS[r.status] || r.status} —</option>
                    {FLOW.slice(curIdx + 1).map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}
                    <option value="cancelled">Cancelled</option>
                  </select>
                  {nextStatus && <p className="mt-1 text-xs text-stone-400">Next step: {LABELS[nextStatus]}</p>}
                </div>
                <div>
                  <label className="p1-label">Provider{suggested ? " — suggested for this service first, all providers below" : ""}</label>
                  <select className="p1-input" value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })} data-testid="sr-provider">
                    <option value="">— Select from directory —</option>
                    {sortedProviders.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.type}{p.verification_status === "verified" ? ", verified" : ""})</option>)}
                  </select>
                  {!form.provider_id && (
                    <input className="p1-input mt-2" placeholder="Or type provider name (not in directory)" value={form.provider_name}
                      onChange={(e) => setForm({ ...form, provider_name: e.target.value })} data-testid="sr-provider-name" />
                  )}
                </div>
                <div>
                  <label className="p1-label">Provider reference / ETA / booking details</label>
                  <input className="p1-input" placeholder="e.g. Booking #1234, ETA 45 min" value={form.provider_ref}
                    onChange={(e) => setForm({ ...form, provider_ref: e.target.value })} data-testid="sr-provider-ref" />
                </div>
                <div>
                  <label className="p1-label">Scheduled date & time</label>
                  <input type="datetime-local" className="p1-input" value={form.scheduled_at}
                    onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="sr-scheduled-at" />
                </div>
                <div>
                  <label className="p1-label">Assign Care Manager (if required)</label>
                  <select className="p1-input" value={form.cm_id} onChange={(e) => setForm({ ...form, cm_id: e.target.value })} data-testid="sr-cm">
                    <option value="">— No change —</option>
                    {cms.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.assigned_families}/{c.max_families} families)</option>)}
                  </select>
                </div>
                <div>
                  <label className="p1-label">Operational note</label>
                  <textarea className="p1-input" rows={2} placeholder="Visible in activity history" value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="sr-note" />
                </div>
                <button onClick={save} disabled={busy} className="w-full rounded-xl bg-emerald-900 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50" data-testid="sr-save">
                  {busy ? "Saving…" : "Save update & notify family"}
                </button>
                <p className="text-xs text-stone-400">The family receives a WhatsApp/email update for every saved change.</p>
              </div>
            </div>
          )}
          {closed && (
            <div className="p1-card p-5" data-testid="sr-closed">
              <p className="text-sm font-semibold text-stone-600">This request is {r.status}. No further actions available.</p>
            </div>
          )}

          <div className="p1-card p-5" data-testid="sr-history">
            <h2 className="font-heading text-base font-semibold text-stone-900">Activity history</h2>
            <ul className="mt-3 space-y-2.5">
              {[...(r.history || [])].reverse().map((h, i) => (
                <li key={i} className="rounded-xl bg-stone-50 p-3 text-xs" data-testid={`sr-history-${i}`}>
                  <span className="font-bold text-stone-800">{LABELS[h.status] || h.status}</span>
                  <span className="text-stone-400"> · {fmtDate(h.at)}{h.by ? ` · by ${h.by}` : ""}</span>
                  {h.note && <p className="mt-0.5 text-stone-600">{h.note}</p>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
